import type { TutorialContent } from './types'

export const DE: TutorialContent = {
    ui: {
        title: 'Anleitung',
        subtitle: 'Alle Tools Schritt für Schritt – mit Tipps und Lösungen',
        searchPlaceholder: 'Anleitung durchsuchen …',
        noResults: 'Keine Treffer – ein anderes Stichwort versuchen.',
        openTool: 'Tool öffnen',
        time: 'Dauer eines Laufs',
        whenToUse: 'Wann nutzen?',
        whatYouGet: 'Das Ergebnis',
        steps: 'Schritt für Schritt',
        options: 'Felder und Optionen',
        behindTheScenes: 'Hinter den Kulissen: So arbeitet das Tool',
        tips: 'Tipps aus der Praxis',
        troubleshooting: 'Wenn etwas nicht klappt',
        related: 'Passt dazu',
        next: 'Weiter',
        glossaryFilter: 'Begriff suchen …',
    },

    nav: {
        start: 'Einstieg',
        groups: [
            { label: 'Überblick & automatisch', tools: ['breaking', 'morning_briefing', 'infographics', 'newsflash'] },
            { label: 'Artikel', tools: ['generator'] },
            { label: 'Kurzmeldungen', tools: ['headlines', 'crypto_headlines', 'single_headline'] },
            { label: 'Quartalszahlen', tools: ['revenue'] },
            { label: 'Prüfen & anreichern', tools: ['formalia', 'seo', 'find_similar'] },
        ],
        reference: 'Nachschlagen',
        pages: {
            welcome: 'Willkommen',
            choose: 'Welches Tool?',
            workflow: 'Ein typischer Tag',
            glossary: 'Glossar',
            troubleshooting: 'Probleme lösen',
        },
    },

    welcome: {
        title: 'Willkommen bei HKCM News',
        intro:
            'HKCM News findet aktuelle Finanznachrichten und macht daraus deutsche Texte im HKCM-Stil – von der Eilmeldung bis zum ausführlichen Artikel mit Faktencheck. Jedes Ergebnis ist ein Entwurf für die Redaktion: prüfen, anpassen, exportieren. Diese Anleitung erklärt jedes Tool Schritt für Schritt: links ein Thema wählen, oben alles durchsuchen.',
        highlightsTitle: 'Die wichtigsten Tools',
        highlights: [
            { tool: 'newsflash', text: 'Eilmeldungen, die von selbst entstehen – alle drei Stunden von 7 bis 19 Uhr, ohne Dubletten.' },
            { tool: 'morning_briefing', text: 'Die Morgenseite öffnet sich von selbst – und aus den Meldungen des Tages wird ein Artikel mit Bildern.' },
            { tool: 'generator', text: 'Ein fertiger Artikel aus Link, Thema oder eigenem Text in zwei bis vier Minuten – mit Faktencheck.' },
            { tool: 'revenue', text: 'Quartalszahlen mit Analystenschätzungen, Ergebnistabelle und TradingView.' },
            { tool: 'formalia', text: 'Jeden Text vor der Veröffentlichung gegen die HKCM-Regeln prüfen.' },
        ],
        quickStartTitle: 'In drei Schritten loslegen',
        quickStart: [
            'Das passende Tool finden: unter „Welches Tool?“ nach Aufgabe suchen oder links ein Tool wählen.',
            'Der Anleitung folgen: Jede Seite erklärt die Schritte, alle Felder und typische Probleme. „Tool öffnen“ springt direkt hinein.',
            'Ergebnis prüfen und exportieren: Zahlen, Namen und Zitate gegen die Quelle prüfen, Formalia kontrollieren, dann als Word exportieren.',
        ],
        rulesTitle: 'Grundregeln',
        rules: [
            {
                name: 'Alles ist ein Entwurf',
                desc: 'Die Tools prüfen Fakten und Formalia, ersetzen aber nicht die redaktionelle Abnahme. Vor dem Veröffentlichen Zahlen, Namen und Zitate gegen die Quelle prüfen.',
            },
            {
                name: 'Keine Anlageberatung',
                desc: 'Keine persönlichen Kauf- oder Verkaufsaufforderungen („Sie sollten jetzt kaufen“) und keine Aussagen, dass etwas „zu Ihnen passt“. Einordnungen bleiben sachlich und auf das Instrument bezogen. Die Tools halten sich daran – beim Bearbeiten bitte auch.',
            },
            {
                name: 'HKCM-Stil',
                desc: 'Deutsch, sachlich und erklärend. Kein Gendern, „Prozent“ statt %, „US-Dollar“ und „Euro“ statt Symbolen. Punkt als Dezimalzeichen wie auf hkcm.com („2.5 Prozent“, „93.65 US-Dollar“), Tausender ab fünf Stellen mit Leerzeichen („86 000“), vierstellige Zahlen zusammen („7764“). Gedankenstrich „ – “, nie „—“ oder „ - “; in erzeugten Texten setzen die Tools ihn selbst. Der Formalia Check prüft das automatisch.',
            },
            {
                name: 'Vor dem Neuladen sichern',
                desc: 'Jedes Tool behält seinen Stand, solange die App geöffnet ist – auch beim Wechsel zu einem anderen Tool und zurück, und ein laufender Auftrag läuft dabei weiter. Neu laden oder das Schließen des Browsers verliert, was noch nicht exportiert ist. Ausnahme: Newsflash speichert alles auf dem Server. Vorher exportieren.',
            },
            {
                name: 'Seitenleiste',
                desc: 'Links stehen alle Tools, nach Aufgaben gruppiert. ⌘B (Mac) bzw. Strg+B (Windows) oder der Knopf oben klappt sie auf die Symbole ein – beim Überfahren zeigt jedes Symbol seinen Namen. Die Adresszeile merkt sich das offene Tool (z. B. …/#/breaking-news): Neu laden oder ein Lesezeichen öffnet direkt dieses Tool.',
            },
            {
                name: 'Gesperrte Seiten',
                desc: 'Manche Seiten sperren automatische Zugriffe oder stehen hinter einer Paywall. Dann den Text selbst einfügen (Artikel-Generator: „Manuell“) oder eine andere Quelle wählen.',
            },
            {
                name: 'Sprache',
                desc: 'Die Oberfläche ist deutsch, nur die Tool-Namen bleiben englisch. Die Texte entstehen immer auf Deutsch. Diese Anleitung gibt es auf Deutsch und Englisch – oben rechts mit „Deutsch“ / „English“ umschalten.',
            },
        ],
    },

    choose: {
        title: 'Welches Tool brauche ich?',
        intro: 'Nach Aufgaben geordnet – ein Klick auf das Tool öffnet seine Anleitung.',
        rows: [
            { need: 'Laufend Eilmeldungen, ohne etwas zu tun', tool: 'newsflash', why: 'Läuft alle drei Stunden von selbst und überspringt, was schon erschienen ist.' },
            { need: 'Sehen, was gerade an den Märkten passiert', tool: 'breaking', why: 'Die wichtigsten Schlagzeilen je Rubrik, sortiert und übersetzt – ideal zur Themensuche.' },
            { need: 'Die Morgenseite vorbereiten', tool: 'morning_briefing', why: 'Fünf Abschnitte beim Öffnen, dazu ein Artikel aus den ausgewählten Meldungen – mit Bildern, bearbeitbar, als Word.' },
            { need: 'Schnell einen fertigen Artikel aus Link oder Thema', tool: 'generator', why: 'Ein Klick, zwei bis vier Minuten, mit Quellenrecherche, Faktencheck und Word-Export.' },
            { need: 'Quartalszahlen in einen Artikel verwandeln', tool: 'revenue', why: 'Kennzahlen aus dem Bericht, Analystenschätzungen und eine Ergebnistabelle.' },
            { need: 'Viele Kurzmeldungen zu einem Thema', tool: 'headlines', why: 'Deutsche Kurzmeldungen aus einer Google-News-Themenseite, oft rund 50 pro Lauf.' },
            { need: 'Krypto-Kurzmeldungen', tool: 'crypto_headlines', why: 'Ohne Link: Google News Krypto, CoinDesk und The Block.' },
            { need: 'Eine einzelne Meldung kurz fassen', tool: 'single_headline', why: 'Schlagzeile und Zusammenfassung in 10 bis 30 Sekunden.' },
            { need: 'Einen Text auf die HKCM-Regeln prüfen', tool: 'formalia', why: 'Längen, Formate und Gendern – mit Bestanden, FAIL und WARN.' },
            { need: 'Einen Text für Google verbessern', tool: 'seo', why: 'Vorschläge nach Priorität, auf Wunsch automatisch umgesetzt.' },
            { need: 'Passende Links setzen', tool: 'find_similar', why: 'HKCM-Archiv, HKCM-Produkte, Web und deutsche Presse – per KI in den Text gesetzt.' },
            { need: 'Grafiken zu einem Artikel', tool: 'infographics', why: 'Infografiken im HKCM-Design als PNG in 4K, 3840 × 2160 Pixel.' },
        ],
    },

    workflow: {
        title: 'Ein typischer Tag',
        intro: 'So greifen die Tools im Alltag ineinander – ein Vorschlag, keine Pflicht.',
        blocks: [
            {
                when: 'Ab 7 Uhr',
                title: 'Den Tag starten',
                items: [
                    { tool: 'newsflash', text: 'Der erste automatische Lauf ist um 7 Uhr – kurz durchsehen, was über Nacht passiert ist.' },
                    { tool: 'morning_briefing', text: 'Vor 9 Uhr öffnen, dann vergleichen Gewinner und Verlierer sauber mit dem Vortag. Meldungen ankreuzen und „Artikel schreiben“ klicken.' },
                    { tool: 'crypto_headlines', text: 'Bei Bedarf ein Krypto-Überblick für den Morgen.' },
                ],
            },
            {
                when: 'Tagsüber',
                title: 'Aktuelles aufgreifen',
                items: [
                    { tool: 'breaking', text: 'Themen sichten. Newsflash ergänzt sich um 10, 13 und 16 Uhr von selbst.' },
                    { tool: 'generator', text: 'Aus einer Meldung schnell einen Artikel machen – den Link aus Breaking News oder Newsflash übernehmen.' },
                    { tool: 'single_headline', text: 'Einzelne Meldungen für Social Media oder Push.' },
                ],
            },
            {
                when: 'In der Berichtssaison',
                title: 'Quartalszahlen',
                items: [
                    { tool: 'revenue', text: 'Pressemitteilung oder PDF einlesen, Kennzahlen prüfen, mit oder ohne TradingView exportieren.' },
                ],
            },
            {
                when: 'Vor dem Veröffentlichen',
                title: 'Qualität sichern',
                items: [
                    { tool: 'formalia', text: 'Formalia prüfen, bis keine FAIL-Befunde mehr übrig sind.' },
                    { tool: 'seo', text: 'SEO-Vorschläge übernehmen – „Kritisch“ zuerst.' },
                    { tool: 'find_similar', text: 'Ein bis zwei interne HKCM-Links und passende Produkte setzen.' },
                    { tool: 'infographics', text: 'Bei Bedarf Grafiken ergänzen.' },
                    { text: 'Zum Schluss Zahlen gegen die Quelle prüfen und als Word exportieren.' },
                ],
            },
            {
                when: '19 Uhr',
                title: 'Tagesabschluss',
                items: [
                    { tool: 'newsflash', text: 'Letzter automatischer Lauf. Für den Tagesrückblick „Nur heute“ wählen und als Word exportieren; die Meldungen bleiben drei Tage abrufbar.' },
                ],
            },
        ],
    },

    tools: {
        breaking: {
            title: 'Breaking News',
            tagline:
                'Die wichtigsten Schlagzeilen aus Google News nach Rubrik – gefiltert, ohne Dubletten, nach Bedeutung sortiert und auf Deutsch.',
            badge: 'Live-Überblick',
            time: '10–20 Sek. je Rubrik',
            whenToUse: [
                'Morgens und zwischendurch sehen, was an den Märkten gerade passiert.',
                'Themen für einen Artikel finden – die Links passen direkt in den Artikel-Generator.',
                'Gezielt einen Bereich prüfen, etwa Deutschland, Krypto oder Rohstoffe.',
            ],
            whatYouGet: [
                'Bis zu 15 Schlagzeilen je Rubrik, auf Deutsch und nach Bedeutung sortiert (Rang 1 ist die wichtigste Meldung).',
                'Unter jeder Schlagzeile die Quelle und das Alter der Meldung („vor 3 Std.“) – ein Klick öffnet den Originalartikel in einem neuen Tab.',
                'Geladene Rubriken bleiben erhalten, solange das Tool geöffnet ist (grüner Punkt an der Rubrik).',
            ],
            steps: [
                {
                    title: 'Rubrik wählen',
                    desc: 'Oben eine Rubrik wählen: „Top-Meldungen“, „USA“, „Deutschland“, „Welt“, „Wirtschaft“, „Technologie“, „Krypto“ oder „Rohstoffe“. Die Rubrik lädt sofort.',
                },
                {
                    title: 'Kurz warten',
                    desc: 'Der Fortschrittsbalken zeigt, was gerade passiert: Meldungen holen, filtern, sortieren, übersetzen. Nach 10 bis 20 Sekunden steht die Liste.',
                },
                {
                    title: 'Liste lesen',
                    desc: 'Die Meldungen sind nach Rang sortiert. Ein Klick auf die Quelle unter einer Schlagzeile öffnet den Originalartikel.',
                },
                {
                    title: 'Alle Rubriken laden (optional)',
                    desc: '„Alle laden“ lädt alle Rubriken, die noch fehlen, vier auf einmal. Bereits geladene werden übersprungen; was nicht geladen werden konnte, nennt danach die Fehlermeldung.',
                },
                {
                    title: 'Aktualisieren',
                    desc: '„Alle aktualisieren“ lädt alle Rubriken neu; die bisherigen Listen bleiben stehen, bis die neuen da sind. Ein erneuter Klick auf die geöffnete Rubrik lädt nur diese neu, ein Klick auf eine andere geladene Rubrik zeigt deren gespeicherte Liste.',
                },
            ],
            options: [
                { name: 'Top-Meldungen', desc: 'Die wichtigsten Nachrichten des Tages, gefiltert auf Relevanz für Märkte und Wirtschaft.' },
                { name: 'USA / Welt', desc: 'US- bzw. internationale Nachrichten, ebenfalls auf Marktrelevanz gefiltert.' },
                { name: 'Deutschland', desc: 'Deutsche Wirtschaftsnachrichten aus deutschsprachigen Quellen; sie werden nicht übersetzt.' },
                { name: 'Wirtschaft / Technologie', desc: 'Wirtschaft bzw. Technologie. Diese Quellen gelten als relevant und werden nicht zusätzlich gefiltert.' },
                { name: 'Krypto / Rohstoffe', desc: 'Kryptowährungen bzw. Rohstoffe wie Gold, Öl und Silber; Rohstoffmeldungen werden auf Marktrelevanz gefiltert.' },
                { name: '„Alle laden“', desc: 'Lädt alle fehlenden Rubriken, vier auf einmal.' },
                { name: '„Alle aktualisieren“', desc: 'Lädt alle Rubriken neu; die alten Listen bleiben bis dahin sichtbar.' },
            ],
            behindTheScenes: [
                'Quelle ist der Google-News-Feed der Rubrik. Liefert er nichts, sucht GPT die aktuellen Schlagzeilen per Websuche.',
                'Bei Top-Meldungen, USA, Welt, Deutschland und Rohstoffen prüft ein schnelles Modell jede Meldung auf Relevanz für Märkte und Wirtschaft.',
                'Meldungen, die älter als 36 Stunden sind, fallen heraus; beim Sortieren zählt auch, wie frisch eine Meldung ist.',
                'Ein weiterer Durchgang fasst doppelte Meldungen zusammen und sortiert nach Bedeutung; die besten 15 werden ins Deutsche übersetzt.',
                'Gespeichert wird nichts: Beim Verlassen des Tools sind die Listen weg.',
            ],
            tips: [
                'Guter Startpunkt für Artikel: Link einer Meldung kopieren und im Artikel-Generator im Modus „URL“ einfügen – Google-News-Links funktionieren dort direkt.',
                'Alle 30 bis 60 Minuten aktualisieren reicht; neue Ereignisse brauchen etwas, bis sie in den Feeds erscheinen.',
                'Eilmeldungen ohne Klicken liefert Newsflash – automatisch alle drei Stunden.',
            ],
            troubleshooting: [
                {
                    q: '„Keine Meldungen gefunden.“',
                    a: 'Für diese Rubrik kam gerade nichts zurück. Einige Minuten warten und erneut versuchen oder eine andere Rubrik wählen.',
                },
                {
                    q: '„Anmeldung abgelaufen“',
                    a: 'Die Anmeldung ist abgelaufen. Im roten Hinweis oben „Erneut anmelden“ klicken und im Fenster anmelden – die Seite und laufende Aufträge bleiben erhalten. Öffnet sich kein Fenster, Pop-ups für die Seite erlauben oder „Abmelden“ klicken und neu anmelden.',
                },
                {
                    q: 'Nach dem Neuladen ist die Liste leer',
                    a: 'Die Listen bleiben erhalten, solange die App geöffnet ist – auch beim Tool-Wechsel. Nach dem Neuladen die Rubrik einfach neu laden.',
                },
            ],
            related: ['newsflash', 'generator', 'morning_briefing'],
        },

        morning_briefing: {
            title: 'Morning Briefing',
            tagline:
                'Die Morgenseite öffnet sich von selbst – aus den ausgewählten Meldungen entsteht ein Artikel mit Bildern, bearbeitbar und als Word.',
            badge: 'Täglicher Entwurf',
            time: 'ca. 40 Sek., Artikel 2–3 Min.',
            whenToUse: [
                'Jeden Morgen: Die Seite erstellt das Briefing beim Öffnen – oder zeigt das, das jemand in der letzten halben Stunde erstellt hat.',
                'Um aus einer oder mehreren Meldungen – oder einem eigenen Thema – den Artikel des Tages zu schreiben.',
                'Wenn der Artikel woanders entsteht: nur die Abschnitte mit „Briefing als Word“ exportieren.',
            ],
            whatYouGet: [
                'Fünf Abschnitte: 1 · Breaking News, 2 · HKCM-Gewinner und -Verlierer, 3 · Die spannendsten Themen, 4 · HKCM-Trades, 5 · HKCM-Signal.',
                'Einen Kasten „Hinweise zur Prüfung“ mit allem, was fehlt oder nicht von heute ist.',
                'Einen Artikel aus bis zu fünf Meldungen oder einem eigenen Thema, im Aufbau der veröffentlichten Morning Briefings: „Guten Morgen!“, zwei bis vier Abschnitte, ein Schluss zur Marktlage, 700 bis 900 Wörter. Die erste Nennung eines HKCM-Titels verlinkt sein Paket.',
                'Die Bilder dazu von selbst: ein fotorealistisches Titelbild und je Abschnitt eine HKCM-Erklärgrafik in 4K mit Bildunterschrift – weitere Grafiken auf Wunsch.',
                'Unter dem Artikel, wie auf hkcm.com: HKCM-Gewinner und -Verlierer, „Die spannendsten Themen“ (drei aktuelle hkcm.com-Artikel), HKCM-Trades und das HKCM-Signal – jeder Block mit „Im Word-Export“.',
                'Einen Word-Export des Artikels (HKCM_Morning_Briefing_Artikel_<Datum>.docx) mit Titelbild, Grafiken, SEO-Block (Meta- und Open-Graph-Texte, Kategorie, Tags) und Quellen, auf Deutsch oder Englisch. Das Briefing selbst gibt es weiter als „Briefing als Word“ und als Markdown.',
            ],
            steps: [
                {
                    title: 'Seite öffnen',
                    desc: 'Das Briefing entsteht von selbst in etwa 40 Sekunden, der Fortschritt wird angezeigt; „Abbrechen“ stoppt den Lauf. Hat jemand in der letzten halben Stunde eines erstellt, erscheint es sofort. „Neu erstellen“ erstellt es mit den eingestellten Optionen neu.',
                },
                {
                    title: 'Zuerst die Hinweise lesen',
                    desc: '„Hinweise zur Prüfung“ nennt, was fehlt oder veraltet sein kann – etwa ein HKCM-Signal vom Vortag („Nicht von heute:“) oder Kurse, die nach 9 Uhr schon den laufenden Tag zeigen.',
                },
                {
                    title: 'Meldungen ankreuzen oder ein eigenes Thema eintragen',
                    desc: 'In Abschnitt 1 bis zu fünf Meldungen ankreuzen – sie werden zu einem Artikel verbunden. Unter „Eigenes Thema (optional)“ geht auch etwas anderes, statt der Auswahl oder zusätzlich. „Zusätzliche Anweisung (optional)“ gibt einen Blickwinkel vor, z. B. „Fokus auf die Folgen für europäische Anleger“.',
                },
                {
                    title: '„Artikel schreiben“ klicken',
                    desc: 'Recherche und Text dauern zwei bis drei Minuten, „Abbrechen“ stoppt sie. Danach entstehen die Bilder von selbst, in ein bis zwei Minuten. Ein neuer Artikel ersetzt den bisherigen erst nach Rückfrage.',
                },
                {
                    title: '„Bearbeiten“ klicken',
                    desc: 'Titel (H1), Teaser, Kernpunkte, Einstieg, jeder Abschnitt, Marktlage und die SEO-Felder werden zu Eingabefeldern – mit Zeichenzahl und Zielbereich. „Fertig“ prüft die Formalia des geänderten Textes neu und zeigt den Artikel wieder so, wie er erscheint; „Zurücksetzen“ holt den geschriebenen Stand zurück, die Bilder bleiben.',
                },
                {
                    title: 'Bilder anpassen',
                    desc: 'Unter jedem Bild: „Neu erstellen“ zeichnet es in einem anderen Aufbau neu, „Entfernen“ nimmt es heraus. „Grafik hinzufügen“ unter einem Abschnitt erstellt eine weitere Grafik – optional mit „Was soll die Grafik zeigen?“ –, einen Absatz weiter unten; bis zu vier je Abschnitt. „Alle Bilder neu erstellen“ zeichnet das Titelbild und die erste Grafik jedes Abschnitts neu; „Stil“ daneben wählt den Look der Grafiken – automatisch, hell oder dunkel.',
                },
                {
                    title: 'Die Blöcke unter dem Artikel wählen',
                    desc: 'HKCM-Gewinner und -Verlierer, „Die spannendsten Themen“, HKCM-Trades und HKCM-Signal haben je „Im Word-Export“. HKCM-Trades ist aus, weil die veröffentlichten Briefings keine Trades zeigen; ein Signal, das nicht von heute ist, ebenfalls. Trades und Signal kommen aus dem Paket des Artikelthemas, das „Thema des Tages“ übernimmt es von selbst.',
                },
                {
                    title: 'Exportieren',
                    desc: 'Im Kopf des Artikels lädt „Word“ mit „DE“ die Word-Datei, mit „EN“ die englische Übersetzung. Wer den Artikel selbst schreibt, nimmt oben „Briefing als Word“: die Abschnitte ohne Artikel, HKCM-Trades und HKCM-Signal so, wie die Seite sie gerade zeigt, die Kurse im Hausformat („+5.83“). „Markdown“ kopiert das Briefing, wie es erstellt wurde.',
                },
                {
                    title: 'Paket für Trades und Signal ändern (optional)',
                    desc: '„Thema des Tages“ eintragen und Enter drücken, ein „HKCM-Paket“ wählen oder unter „Passt auch:“ klicken: Abschnitt 4 und 5 laden neu, das übrige Briefing und der Artikel bleiben.',
                },
            ],
            options: [
                { name: 'Thema des Tages', desc: 'Stichwort, das das HKCM-Paket für HKCM-Trades und HKCM-Signal bestimmt. Leer übernimmt es das Hauptthema des Artikels – und beim nächsten Artikel dessen Thema; ein selbst eingetragenes bleibt. Enter lädt Abschnitt 4 und 5 neu.' },
                { name: 'HKCM-Paket', desc: '„Automatisch – passend zum Thema“ oder ein fest gewähltes Paket.' },
                { name: 'Breaking News', desc: 'Wie viele Meldungen Abschnitt 1 zeigt (1–10, Standard 5). Gilt ab „Neu erstellen“.' },
                { name: 'Gewinner / Verlierer', desc: 'Wie viele Titel je Seite Abschnitt 2 zeigt (1–10, Standard 3).' },
                { name: 'HKCM-Themen', desc: 'Wie viele Themen Abschnitt 3 zeigt (1–10, Standard 3).' },
                { name: 'Eigenes Thema', desc: 'Ein Thema, zu dem der Artikel recherchiert wird – statt der ausgewählten Meldungen oder zusätzlich.' },
                { name: 'Zusätzliche Anweisung', desc: 'Gilt für den Artikel, z. B. ein Blickwinkel oder eine Zielgruppe.' },
                { name: '„Grafik hinzufügen“', desc: 'Eine weitere Erklärgrafik für einen Abschnitt, mit anderem Aufbau und eigener Bildunterschrift. „Was soll die Grafik zeigen?“ gibt den Schwerpunkt vor; jede Zahl muss im Abschnitt stehen. Höchstens vier Grafiken je Abschnitt; ein Abschnitt ohne Text bekommt keine. Alle Grafiken in 4K mit HKCM-Logo, im Stil der Grafiken auf hkcm.com: hell wie die Morning-Briefing-Grafiken oder als dunkles Poster wie die Krypto- und Crash-Serien. Bei „Stil: automatisch“ schlägt das Modell den Look je Artikel vor; alle Grafiken eines Artikels behalten ihn, auch neu erstellte und hinzugefügte.' },
            ],
            behindTheScenes: [
                'Abschnitt 1: aktuelle Meldungen aus Google News – vom letzten Tag, montags inklusive Wochenende –, zu Geschichten gebündelt und nach Bedeutung für das HKCM-Universum sortiert. „N Medien“ zählt verschiedene Medien.',
                'Abschnitt 2: Kurse der rund 180 Titel der HKCM-Watchlist auf TradingView – die größten Gewinner und Verlierer.',
                'Abschnitt 3 bis 5: Beiträge, Trades und Ankündigungen aus den HKCM-Inhalten. Trades erscheinen erst nach einer Sperrfrist von fünf Tagen; das HKCM-Signal schreibt die Ankündigung vom Vortag für heute um. Seit HKCMs Relaunch am 18.09.2026 gibt es diese Ankündigung nicht mehr: Das Signal nennt dann die Analysen, die HKCM im Paket an diesem Wochentag veröffentlicht – laut Wochenplan („Nach Wochenplan:“, mit dem heutigen Plan abgleichen), sonst die letzten Analysen des Pakets mit ihrem Datum. Ebenfalls seit dem 18.09.2026 legt die Datenbank Trades unter dem Titel statt unter dem Paket ab – sie werden über frühere HKCM-Meldungen und den Produktkatalog zugeordnet und als „zugeordnet“ markiert.',
                'Das Modell wählt nur aus den gelieferten Daten aus und erfindet keine Quellen. Fällt ein Abschnitt aus, bleibt der Rest stehen, und der Ausfall steht in den Hinweisen.',
                'Der Artikel: GPT-6 recherchiert jede Meldung per Websuche und verbindet alle zu einem Text im Aufbau der veröffentlichten Morning Briefings; es prüft seine Quellen und hält die Formalia ein. Die Links auf die HKCM-Pakete setzt das Programm aus dem Produktkatalog, nicht das Modell.',
                '„Die spannendsten Themen“: drei aktuelle Artikel aus dem hkcm.com-Archiv. Das Modell wählt nur aus – Titel und Links stammen aus dem Archiv.',
                'Grafiken: Ein Modell schreibt den Text jeder Grafik aus dem Abschnitt, und jede Zahl muss dort stehen. Das Bildmodell zeichnet die Grafik, ein zweites Modell liest sie gegen und lässt sie bei einem Schreibfehler einmal neu zeichnen. Zwei Grafiken eines Artikels haben nie denselben Aufbau; das HKCM-Logo wird danach eingesetzt, nicht gezeichnet.',
            ],
            tips: [
                'Vor 9 Uhr öffnen – dann vergleicht Abschnitt 2 sauber mit dem Vortag.',
                'Mehrere Meldungen zu einem Thema, etwa Fed, Yen und Bitcoin, werden ein Artikel mit einem Abschnitt je Meldung.',
                'Neu laden schadet nicht: Der Artikel kommt mit allen Änderungen und Bildern zurück, ein laufender Schreibauftrag wird weiter verfolgt, HKCM-Trades und HKCM-Signal laden für sein Thema – in diesem Browser und für heute. „Verwerfen“ im Hinweis nimmt ihn von der Seite.',
            ],
            troubleshooting: [
                {
                    q: 'Abschnitt 4 und 5 sind leer',
                    a: 'Sie gehören zum Paket des Tagesthemas und erscheinen mit dem Artikel. Ohne Artikel ein „Thema des Tages“ eintragen und Enter drücken oder ein Paket wählen.',
                },
                {
                    q: '„HKCM-Inhalte sind nicht konfiguriert …“',
                    a: 'Dem Server fehlt der Zugang zu den HKCM-Inhalten, deshalb bleiben Abschnitt 3 bis 5 leer. Breaking News und Gewinner/Verlierer funktionieren. Bitte der Technik melden.',
                },
                {
                    q: '„Der Briefing-Auftrag ist nicht mehr erreichbar.“',
                    a: 'Der Server wurde während des Laufs neu gestartet. Das Briefing neu erstellen.',
                },
                {
                    q: 'Der Artikel ist fehlgeschlagen',
                    a: 'Die Auswahl steht noch – „Artikel schreiben“ noch einmal klicken.',
                },
                {
                    q: '„zugeordnet“ bei einem Trade',
                    a: 'Die HKCM-Datenbank hat den Trade ohne Paket geliefert; er wurde über frühere Meldungen zugeordnet. Vor der Veröffentlichung prüfen, ob er zum Paket gehört – die Hinweise nennen Titel, die mehrere Pakete führen.',
                },
                {
                    q: '„Bitte die Schreibweise prüfen: …“ unter einer Grafik',
                    a: 'Das Gegenlesen hat ein Wort gefunden, das nicht wie geplant in der Grafik steht, auch nach dem zweiten Versuch. Die Grafik ansehen und bei einem Fehler „Neu erstellen“ klicken.',
                },
                {
                    q: '„Die Schreibweise wurde nicht mehr gegengelesen …“ unter einer Grafik',
                    a: 'Die Grafik wurde so spät fertig, dass für das Gegenlesen keine Zeit blieb. Ihre Texte selbst lesen und bei einem Fehler „Neu erstellen“ klicken.',
                },
                {
                    q: '„Das Bild wurde nicht rechtzeitig fertig …“',
                    a: 'Unter dem fehlenden Bild „Neu erstellen“ klicken – die übrigen Bilder bleiben.',
                },
                {
                    q: '„In einem anderen Tab gespeichert“',
                    a: 'Der Artikel ist in zwei Tabs offen, und der andere hat eine neuere Fassung gespeichert. Diese Seite speichert dann nichts mehr, damit sie nicht verloren geht. „Neueren Stand laden“ übernimmt die Fassung aus dem anderen Tab, „Diesen Stand behalten“ speichert die Fassung dieser Seite darüber.',
                },
            ],
            related: ['breaking', 'newsflash', 'generator'],
        },

        newsflash: {
            title: 'Newsflash',
            tagline:
                'Automatische Eilmeldungen: Alle drei Stunden von 7 bis 19 Uhr entstehen aus neuen Breaking- und Krypto-Meldungen kurze deutsche Texte – nichts doppelt, drei Tage abrufbar.',
            badge: 'Automatisch',
            time: 'läuft von selbst · von Hand bis ca. 1 Min.',
            whenToUse: [
                'Wenn fortlaufend kurze Eilmeldungen gebraucht werden, ohne selbst etwas anzustoßen.',
                'Um schnell zu sehen, was seit dem letzten Blick neu ist.',
                'Als Vorlage für CMS, Newsletter oder Social Media.',
            ],
            whatYouGet: [
                'Einen Feed nach Tagen gruppiert: Uhrzeit, Kennzeichen „Breaking News“ oder „Krypto“, deutsche Überschrift, drei Sätze und die Quelle.',
                'Je Lauf bis zu zwölf Breaking-News- und sechs Krypto-Meldungen – so viele, wie wichtige Meldungen da sind, nie Füllmaterial.',
                'Einen Word-Export für heute oder die letzten drei Tage, auf Wunsch nur für eine Rubrik – auf Deutsch oder Englisch.',
            ],
            steps: [
                {
                    title: 'Newsflash öffnen',
                    desc: 'Der Feed erscheint sofort. Oben steht, wann der letzte Lauf war und wie viele Meldungen er brachte („Letzter Lauf: … – 3 neu“), und wann der nächste startet.',
                },
                {
                    title: 'Filtern',
                    desc: '„Alle“, „Breaking News“ oder „Krypto“ wählen – die Zahl daneben zeigt, wie viele Meldungen es gibt.',
                },
                {
                    title: 'Meldung übernehmen',
                    desc: '„Kopieren“ legt Überschrift, Text und Quelle in die Zwischenablage. Der Quellen-Link öffnet den Originalartikel.',
                },
                {
                    title: 'Sofort prüfen (optional)',
                    desc: '„Jetzt prüfen“ startet sofort einen Lauf, mit Fortschrittsbalken. Läuft schon einer, zeigt die Seite „Ein Lauf ist gerade aktiv …“ und aktualisiert sich danach selbst.',
                },
                {
                    title: 'Als Word herunterladen',
                    desc: 'Zeitraum wählen („Letzte 3 Tage“ oder „Nur heute“) und bei „Word“ auf „DE“ oder „EN“ klicken. Exportiert wird die gerade gewählte Rubrik.',
                },
            ],
            options: [
                { name: '„Jetzt prüfen“', desc: 'Startet sofort einen Lauf, zusätzlich zu den geplanten.' },
                { name: 'Filter', desc: 'Alle, Breaking News oder Krypto; gilt auch für den Word-Export.' },
                { name: 'Zeitraum', desc: '„Letzte 3 Tage“ oder „Nur heute“ für den Word-Export.' },
                { name: '„Word“ – „DE“ / „EN“', desc: 'Lädt HKCM_Newsflash_<Datum>.docx herunter, mit „EN“ die englische Übersetzung (…_EN.docx).' },
                { name: 'Statuszeile', desc: 'Letzter Lauf (mit Anzahl neuer Meldungen oder „fehlgeschlagen“) und nächster geplanter Lauf.' },
            ],
            behindTheScenes: [
                'Geplant um 7, 10, 13, 16 und 19 Uhr deutscher Zeit – auf dem Server, auch wenn niemand die Seite geöffnet hat. Ist ein Termin ausgefallen, holt das Öffnen der Seite ihn nach.',
                'Quellen: die Google-News-Rubriken von Breaking News, dazu Google-News-Suchen nach Märkten, Konjunktur und Notenbanken, Quartalszahlen, Börse und Krypto, außerdem CoinDesk, The Block, Cointelegraph, Decrypt und Bitcoin Magazine. Fällt Google News aus, sucht GPT die Meldungen per Websuche.',
                'Berücksichtigt werden nur Meldungen der letzten 14 Stunden. Gleiche Links und Überschriften fallen sofort heraus; ein Modell erkennt außerdem dieselbe Geschichte aus verschiedenen Quellen und alles, was schon veröffentlicht wurde.',
                'Jede Meldung wird per Websuche recherchiert und im HKCM-Stil geschrieben, ohne Anlageempfehlung. Liegt das Ereignis mehr als zwei Tage zurück, wird es dauerhaft übersprungen.',
                'Es läuft immer nur ein Lauf gleichzeitig. Gespeichert wird drei Tage, ältere Meldungen verschwinden automatisch.',
                'Was nicht erscheinen darf, bleibt draußen: Eine Meldung mit persönlicher Handlungsaufforderung, einer um den Faktor tausend falschen Zahl, einem „du“ oder Gendern wird nicht veröffentlicht – der nächste Lauf versucht sie neu.',
            ],
            tips: [
                '„Jetzt prüfen“ ist selten nötig – der Feed füllt sich von selbst.',
                'Vor dem Veröffentlichen Zahlen und Namen gegen die Quelle prüfen; die Texte sind Entwürfe.',
                'Für einen ausführlichen Artikel den Quellen-Link in den Artikel-Generator übernehmen.',
            ],
            troubleshooting: [
                {
                    q: '„Keine neuen Eilmeldungen seit dem letzten Lauf.“',
                    a: 'Es gab nichts Neues, oder alles Neue war schon veröffentlicht. Das ist normal – der nächste Lauf prüft erneut.',
                },
                {
                    q: '„Ein anderer Lauf ist gerade aktiv …“',
                    a: 'Ein geplanter Lauf oder jemand aus dem Team hat gerade einen gestartet. Warten – die Seite aktualisiert sich, sobald er fertig ist.',
                },
                {
                    q: '„Im gewählten Zeitraum gibt es keine Meldungen.“',
                    a: 'Für diesen Zeitraum und diese Rubrik gibt es nichts zu exportieren. „Letzte 3 Tage“ oder den Filter „Alle“ wählen.',
                },
                {
                    q: 'Die Statuszeile zeigt „fehlgeschlagen“',
                    a: 'Der Lauf ist abgebrochen, etwa weil keine Quelle erreichbar war. Die Hinweise darunter nennen den Grund. Der nächste Lauf versucht es erneut, oder „Jetzt prüfen“ klicken.',
                },
                {
                    q: 'Ältere Meldungen sind verschwunden',
                    a: 'Gewollt: Meldungen werden drei Tage gespeichert. Wer sie länger braucht, exportiert sie vorher als Word.',
                },
                {
                    q: '„Der Lauf wurde unterbrochen, bevor ein Ergebnis kam.“',
                    a: 'Die Verbindung zum Server endete vor dem Ergebnis – eine Anfrage darf höchstens zwei Minuten dauern. „Jetzt prüfen“ noch einmal klicken; schon veröffentlichte Meldungen erscheinen nicht doppelt.',
                },
            ],
            related: ['breaking', 'crypto_headlines', 'generator'],
        },

        generator: {
            title: 'Article Generator',
            tagline:
                'Aus einer URL, einem Suchthema oder eigenem Text wird in zwei bis vier Minuten ein vollständiger deutscher HKCM-Artikel – mit Quellenrecherche, Formalia-Prüfung und Faktencheck.',
            badge: 'Vollständiger Artikel',
            time: 'ca. 2–4 Min.',
            whenToUse: [
                'Wenn aus einer Meldung schnell ein fertiger Artikel werden soll.',
                'Wenn mehrere Quellen zu einem Thema zusammengeführt werden sollen (Eingabe „Suchbegriff“).',
                'Wenn eigener Text, Notizen oder ein Artikel hinter einer Paywall in den HKCM-Stil sollen (Eingabe „Manuell“).',
            ],
            whatYouGet: [
                'Titel (H1), Teaser, drei „Kernpunkte“, Text mit Zwischenüberschriften und – mit „FAQ erzeugen“ – eine FAQ.',
                'SEO-Felder: Meta-Titel, Meta-Beschreibung, Kategorie (eine der sieben CMS-Kategorien) und Tags.',
                'Eine Formalia-Plakette (Bestanden, FAIL oder WARN) mit den wichtigsten Kennzahlen.',
                'Eine Karte „Faktencheck“: „Geprüft – keine Beanstandungen“ oder die Zahl der Hinweise („2 Hinweise“), jeder mit Gewicht (Hoch, Mittel, Gering), der beanstandeten Aussage und dem Grund – oder „Nicht geprüft“, wenn der Faktencheck nicht gelaufen ist.',
                'Rechts die verwendeten „Quellen“.',
                'Auf Wunsch Titelbild, News-Grafiken und Stimmungsanalyse; Export als Word-Bericht.',
            ],
            steps: [
                {
                    title: 'Eingabe wählen',
                    desc: 'Links unter „Eingabe“:\n• „URL“ – Link zu einem Artikel unter „Artikel-URL“. Über „URL hinzufügen“ bis zu fünf Links; das Symbol im Feld fügt aus der Zwischenablage ein.\n• „Suchbegriff“ – ein Thema oder eine Frage, z. B. „Warum fällt die Nvidia-Aktie heute?“.\n• „Manuell“ – „Titel“, „Quellenlink“ und den vollständigen „Artikeltext“ einfügen; alle drei Felder sind Pflicht.',
                },
                {
                    title: 'Optionen setzen (optional)',
                    desc: '„Ausgabe“: „Vollständiger Artikel“ oder „Überblick (nur Kernfakten)“. „Tonalität“: leer lassen für den neutralen HKCM-Ton. „FAQ erzeugen“ ankreuzen, wenn der Artikel eine FAQ bekommen soll – nur bei „Vollständiger Artikel“, standardmäßig aus. „News-Grafiken erstellen“ ankreuzen, wenn Grafiken dazukommen sollen.',
                },
                {
                    title: '„Artikel erstellen“ klicken',
                    desc: 'Der Knopf steht unten im linken Bereich. Der Fortschrittsbalken nennt den Schritt – „Quellensuche läuft …“, „Artikel wird geschrieben …“, „Faktencheck läuft …“ –, darunter läuft das „Protokoll“. Nach zwei bis vier Minuten steht „Fertig“. „Abbrechen“ neben dem Balken beendet den Lauf; lehnt der Server das ab, nennt die Seite den Grund, und der Lauf geht weiter.',
                },
                {
                    title: 'Ergebnis prüfen',
                    desc: 'Zuerst die Formalia-Plakette: FAIL vor der Veröffentlichung beheben, WARN ist eine Empfehlung. Darunter die Karte „Faktencheck“: Jeder Hinweis nennt die Aussage und warum sie fraglich ist – mit den Quellen abgleichen und korrigieren. Dann Titel, Teaser, Kernpunkte, Text, gegebenenfalls die FAQ und „SEO und Metadaten“ lesen. Rechts unter „Quellen“ stehen die Quellen des Artikels; „Quelle öffnen“ zeigt den Originalartikel.',
                },
                {
                    title: 'Bearbeiten',
                    desc: '„Bearbeiten“ im Kopf der Artikelkarte macht Titel, Teaser, Kernpunkte, Text und FAQ editierbar. Eine Stelle im Titel oder Text markieren, „Mit KI bearbeiten“ klicken, eine Anweisung eingeben (z. B. „kürzer und sachlicher“), „Vorschlag erstellen“ und nach der Prüfung „Übernehmen“. „Rückgängig“ nimmt Änderungen am Text zurück, „Fertig“ beendet die Bearbeitung.',
                },
                {
                    title: 'Extras (optional)',
                    desc: 'Oben rechts: „Titelbild erstellen“ erzeugt ein Titelbild aus Titel und Text; unter „Eigene Vorgaben (optional)“ lässt sich ein Wunsch ergänzen (z. B. „Ölfass im Mittelpunkt“). „Neu erstellen“, bis es passt, dann „Übernehmen“. „Stimmungsanalyse“ ordnet die Marktstimmung ein (Bullish, Bearish oder Neutral, mit betroffenen Aktien) und lässt sich als Word speichern. News-Grafiken erscheinen unter den Kernpunkten, jede als PNG ladbar.',
                },
                {
                    title: 'Exportieren',
                    desc: '„Word“ („DE“ oder „EN“) oben rechts lädt alles für das CMS: Titel, Titelbild, Teaser, Meta- und Social-Media-Felder, „Das Wichtigste in Kürze“, Text mit Grafiken, FAQ (falls erzeugt), SEO und die Quellen.',
                },
            ],
            options: [
                { name: 'URL', desc: 'Bis zu fünf Artikel-Links. Der erste ist die Hauptquelle; weitere Berichte zum Thema sucht das Tool selbst.' },
                { name: 'Suchbegriff', desc: 'Ein Thema oder eine konkrete Frage. Das Tool sucht die passenden aktuellen Berichte selbst – je konkreter, desto besser.' },
                { name: 'Manuell', desc: 'Für gesperrte Seiten, Paywalls oder eigene Texte: Titel, Quellenlink und vollständiger Artikeltext.' },
                { name: 'Ausgabe', desc: '„Vollständiger Artikel“ schreibt den ganzen Artikel. „Überblick (nur Kernfakten)“ liefert nur Hauptthema, Tonlage, Kernfakten und genannte Unternehmen – gut, um ein Thema schnell einzuschätzen.' },
                { name: 'Tonalität', desc: 'Optional; leer bedeutet der neutrale HKCM-Ton. Beispiel: „vorsichtig, mit Blick auf Risiken“.' },
                { name: 'FAQ erzeugen', desc: 'Nur bei „Vollständiger Artikel“, standardmäßig aus: „Bis zu 4 Fragen mit Antworten für das FAQ-Feld im CMS.“ Ohne Häkchen hat der Artikel keine FAQ – weder auf der Seite noch in der Word-Datei.' },
                { name: 'News-Grafiken erstellen', desc: 'Erstellt nach dem Artikel Infografiken im HKCM-Design – „Automatisch (2 bis 3)“ oder 1 bis 4, optional mit Quellenangabe. Sie landen auch im Word-Bericht.' },
            ],
            behindTheScenes: [
                'Quellen: Die Hauptquelle wird gelesen, bei Sperren über einen Browser oder per GPT-Recherche. Dazu sucht GPT per Websuche weitere aktuelle Berichte – bei „URL“ bis zu fünf, bei „Manuell“ bis zu sechs, bei „Suchbegriff“ bis zu 15. Langsame Seiten bremsen nicht: Nach etwa 20 Sekunden geht es mit den vorhandenen Quellen weiter.',
                'Schreiben: Das stärkste Modell schreibt den Artikel in einem Durchgang direkt auf Deutsch aus den Originalquellen, im HKCM-Stil und nach den Formalia. Die Kategorie ist eine der sieben CMS-Kategorien; eine FAQ entsteht nur mit „FAQ erzeugen“.',
                'Prüfen: Formate werden vereinheitlicht (Prozent, US-Dollar, Zahlen, Gedankenstriche), die Formalia geprüft und Zahlen, Daten und Zitate mit den Quellen abgeglichen. Eindeutige Fehler werden automatisch korrigiert; das Protokoll zeigt „Faktencheck fertig (…)“, die Karte „Faktencheck“ jeden Befund.',
                'Titelbild: ein Nachrichtenbild, wie es Agenturen wie Reuters oder dpa zu der Meldung bringen – hell und aktuell, mit dem Namen des Themas groß im Bild (das Logo eines Unternehmens an seiner Zentrale, der Name eines Index oder Rohstoffs auf einem Bildschirm) und, wenn es um einen Kurs geht, einem Chart auf einem echten Bildschirm, dessen Verlauf dem Artikel folgt –, 16:9 in 4K mit 3840 × 2160 Pixeln (JPEG). Das Motiv steht in der rechten Hälfte; die linke bleibt ruhig, weil hkcm.com dort die weiße Überschrift setzt. Kein HKCM-Logo – HKCM setzt nie eins auf ein Titelbild. Personen nur von hinten, aus der Ferne oder unscharf, keine erkennbaren realen Personen; keine lesbaren Zahlen, lesbar ist nur der Name.',
                'Der Auftrag läuft auf dem Server. Andere Tools lassen sich zwischendurch öffnen; nur beim Neuladen oder Schließen des Browser-Tabs geht die Verbindung verloren.',
            ],
            tips: [
                'Gesperrte Seite oder Paywall? Gleich „Manuell“ nehmen und den Text einfügen.',
                'Bei „Suchbegriff“ eine konkrete Frage stellen („Warum fällt die Nvidia-Aktie heute?“) statt eines einzelnen Stichworts.',
                'Links aus Breaking News und Newsflash, auch Google-News-Links, funktionieren direkt.',
                'Nach größeren Änderungen den Text im Formalia Check noch einmal prüfen.',
            ],
            troubleshooting: [
                {
                    q: '„Der Artikel unter der URL konnte nicht geladen werden …“',
                    a: 'Die Seite blockiert automatische Zugriffe oder hat eine Paywall. Artikel im Browser öffnen, Text kopieren und über die Eingabe „Manuell“ einfügen.',
                },
                {
                    q: 'Zeitüberschreitung bei der Quellensuche',
                    a: 'Einzelne Quellen waren zu langsam. Noch einmal starten oder die Eingabe „Manuell“ nutzen.',
                },
                {
                    q: 'Aus den Quellen kam kein brauchbarer Text',
                    a: 'Die Seiten lieferten keinen lesbaren Inhalt, etwa nur Cookie-Hinweise. Eine andere URL wählen oder „Manuell“ nutzen.',
                },
                {
                    q: '„Der Server wurde neu gestartet, der Auftrag ist verloren …“',
                    a: 'Der Server wurde während des Laufs neu gestartet. Den Artikel einfach neu erstellen.',
                },
                {
                    q: '„Der Artikel konnte nicht gestartet werden …“ oder „Anmeldung abgelaufen“',
                    a: 'Meist ist die Anmeldung abgelaufen: im roten Hinweis oben „Erneut anmelden“, dann erneut starten.',
                },
                {
                    q: 'Die Plakette zeigt FAIL',
                    a: 'Der Befund nennt Regel und Stelle, z. B. ein Prozentzeichen oder „10.000“ statt „10 000“. Mit „Bearbeiten“ korrigieren.',
                },
                {
                    q: 'Der Artikel hat keine FAQ',
                    a: '„FAQ erzeugen“ war nicht angekreuzt – ohne Häkchen entsteht keine FAQ. Ankreuzen und den Artikel neu erstellen.',
                },
            ],
            related: ['formalia', 'seo', 'find_similar', 'infographics'],
        },

        headlines: {
            title: 'Headlines Generator',
            tagline:
                'Aus einer Google-News-Themenseite werden automatisch deutsche Kurzmeldungen – Schlagzeile plus drei Sätze, oft rund 50 pro Lauf.',
            badge: 'Viele Kurzmeldungen',
            time: 'ca. 1,5–3 Min.',
            whenToUse: [
                'Für einen Nachrichtenüberblick zu einem Thema, etwa Wirtschaft, Märkte oder eine Branche.',
                'Für Newsletter, Ticker oder Social Media, wenn viele kurze Meldungen gebraucht werden.',
            ],
            whatYouGet: [
                'Karten mit deutscher Schlagzeile (bis 60 Zeichen) und einer Zusammenfassung von drei Sätzen.',
                'Zu jeder Karte die Quelle; rechts alle gefundenen Links („Gefundene Artikel“).',
                'Einen Word-Export mit allen Meldungen, auf Deutsch oder Englisch (HKCM_Headlines_<Datum>.docx).',
            ],
            steps: [
                {
                    title: 'Themenseite einfügen',
                    desc: 'Das Feld „Google-News-Seite“ ist mit der Rubrik Wirtschaft vorbelegt. Für ein anderes Thema news.google.com öffnen, das Thema aufrufen und die Adresse aus der Browserleiste einfügen. Es funktionieren nur Google-News-Links.',
                },
                {
                    title: '„Headlines erstellen“ klicken',
                    desc: 'Der Fortschrittsbalken nennt die Phase: „Schlagzeilen werden gesammelt …“, „Relevanz wird geprüft (KI) …“, „Dubletten werden zusammengefasst (KI) …“, „Geschichten werden recherchiert und geschrieben …“. Darunter läuft das „Protokoll“ mit Uhrzeiten. „Abbrechen“ neben dem Balken beendet den Lauf.',
                },
                {
                    title: 'Karten lesen',
                    desc: 'Die Meldungen erscheinen nach und nach unter „Kurzmeldungen“ (z. B. „12 von 48 erstellt“). Ein Klick auf eine Karte klappt die Zusammenfassung auf.',
                },
                {
                    title: 'Bearbeiten und übernehmen',
                    desc: 'Je Karte: „Bearbeiten“ macht Schlagzeile und Text editierbar; eine Stelle markieren und „Mit KI bearbeiten“ für KI-Hilfe; „Fertig“ beendet. „Kopieren“ kopiert die Meldung, „Quelle öffnen“ zeigt den Originalartikel.',
                },
                {
                    title: 'Exportieren',
                    desc: '„Word“ („DE“ oder „EN“) lädt alle Karten als „HKCM Headlines“ herunter – je Meldung Schlagzeile, Zusammenfassung und Quellenlink.',
                },
            ],
            options: [
                { name: 'Google-News-Seite', desc: 'Eine Themen- oder Rubrikseite von Google News; das X („Feld leeren“) leert das Feld.' },
                { name: '„Abbrechen“', desc: 'Beendet den Lauf; steht während des Laufs neben dem Fortschrittsbalken.' },
                { name: '„Word“ – „DE“ / „EN“', desc: 'Alle angezeigten Karten als Word-Datei, auf Deutsch oder ins Englische übersetzt.' },
                { name: 'Knöpfe je Karte', desc: '„Bearbeiten“ / „Fertig“, „Mit KI bearbeiten“, „Kopieren“ und „Quelle öffnen“.' },
            ],
            behindTheScenes: [
                'Die Themenseite wird ausgelesen; ein Modell sortiert Irrelevantes aus und fasst doppelte Geschichten zusammen.',
                'Was in den letzten zehn Tagen schon als Kurzmeldung entstanden ist, wird übersprungen – das Protokoll meldet dann „… Geschichte(n) übersprungen: schon in den letzten 10 Tagen verarbeitet.“',
                'Für jede Geschichte recherchiert das Modell per Websuche und schreibt Schlagzeile und Text in einem Schritt; kommt nichts Brauchbares zurück, wird einmal nachgebessert.',
            ],
            tips: [
                'Konkrete Themenseiten, etwa zu einer Branche, liefern treffendere Meldungen als die allgemeine Wirtschaftsseite.',
                'Ein Lauf morgens und einer nachmittags reichen – ein weiterer Lauf zeigt nur Neues.',
                'Es gibt keinen Löschknopf: Unpassende Meldungen im Word-Dokument entfernen.',
                'Neu laden verliert die Karten – vorher exportieren. Beim Tool-Wechsel bleiben sie erhalten.',
            ],
            troubleshooting: [
                {
                    q: '„Fertig (keine neuen Geschichten)“',
                    a: 'Alle Geschichten der Seite wurden in den letzten zehn Tagen schon verarbeitet. Später erneut versuchen oder eine andere Themenseite nehmen.',
                },
                {
                    q: 'Nur wenige Karten',
                    a: 'Die Themenseite hatte wenige relevante oder neue Geschichten. Eine breitere Seite wählen.',
                },
                {
                    q: 'Eine Karte ist leer oder dünn',
                    a: 'Die Recherche fand nichts Verlässliches. Über „Quelle öffnen“ den Originalartikel lesen oder die Meldung weglassen.',
                },
            ],
            related: ['crypto_headlines', 'single_headline', 'newsflash'],
        },

        crypto_headlines: {
            title: 'Crypto Headlines',
            tagline: 'Ein Klick liefert aktuelle Krypto-Kurzmeldungen auf Deutsch – aus Google News Krypto, CoinDesk und The Block.',
            badge: 'Nur Krypto',
            time: 'ca. 1–2 Min.',
            whenToUse: [
                'Für den täglichen Krypto-Überblick, ohne eine Themenseite suchen zu müssen.',
                'Für Krypto-Newsletter oder -Ticker.',
            ],
            whatYouGet: [
                'Karten mit deutscher Schlagzeile und drei Sätzen, jeweils mit Quelle.',
                'Einen Word-Export mit allen Meldungen, auf Deutsch oder Englisch (HKCM_Crypto_Headlines_<Datum>.docx).',
            ],
            steps: [
                {
                    title: '„Headlines erstellen“ klicken',
                    desc: 'Ein Link ist nicht nötig – das Tool holt die Meldungen aus Google News Krypto, CoinDesk und The Block.',
                },
                {
                    title: 'Fortschritt verfolgen',
                    desc: 'Dieselben Phasen wie beim Headlines Generator: sammeln, auf Relevanz filtern, Dubletten zusammenfassen, recherchieren und schreiben. „Abbrechen“ neben dem Fortschrittsbalken beendet den Lauf.',
                },
                {
                    title: 'Karten lesen und bearbeiten',
                    desc: 'Karte aufklappen. „Bearbeiten“, „Mit KI bearbeiten“, „Kopieren“ und „Quelle öffnen“ funktionieren wie beim Headlines Generator.',
                },
                {
                    title: 'Exportieren',
                    desc: '„Word“ („DE“ oder „EN“) lädt alle Meldungen herunter.',
                },
            ],
            options: [
                { name: '„Headlines erstellen“', desc: 'Startet den Lauf ohne weitere Eingaben.' },
                { name: '„Abbrechen“ / „Word“', desc: 'Abbrechen bzw. alle Karten als Word-Datei, auf Deutsch oder Englisch.' },
            ],
            behindTheScenes: [
                'Quellen sind Google News Krypto, CoinDesk und The Block. Ein Modell filtert auf Relevanz und fasst doppelte Geschichten zusammen.',
                'Was in den letzten zehn Tagen schon als Krypto-Meldung entstanden ist, wird übersprungen – unabhängig vom Headlines Generator.',
                'Jede Geschichte wird per Websuche recherchiert und in einem Schritt geschrieben.',
            ],
            tips: [
                'Automatische Krypto-Eilmeldungen ohne Klick liefert Newsflash mit dem Filter „Krypto“.',
                'Kurse vor dem Veröffentlichen prüfen – im Kryptomarkt ändern sie sich schnell.',
            ],
            troubleshooting: [
                {
                    q: '„Fertig (keine neuen Geschichten)“',
                    a: 'Alle aktuellen Krypto-Geschichten wurden in den letzten zehn Tagen schon verarbeitet. Später erneut versuchen.',
                },
            ],
            related: ['headlines', 'newsflash', 'single_headline'],
        },

        single_headline: {
            title: 'Single Headline',
            tagline: 'Einen Artikel-Link einfügen – nach 10 bis 30 Sekunden gibt es eine deutsche HKCM-Schlagzeile und eine kurze Zusammenfassung.',
            badge: 'Schnell',
            time: '10–30 Sek.',
            whenToUse: [
                'Für eine einzelne Meldung, etwa für Social Media, Push oder einen Ticker.',
                'Um schnell zu erfassen, worum es in einem englischen Artikel geht.',
            ],
            whatYouGet: [
                'Eine deutsche Schlagzeile (bis 60 Zeichen) und eine Zusammenfassung von drei Sätzen, mit Link zur Quelle.',
                'Einen Word-Export auf Deutsch oder Englisch (HKCM_Headlines_<Datum>.docx).',
            ],
            steps: [
                {
                    title: 'Link einfügen',
                    desc: 'Den Link zu einem Nachrichtenartikel einfügen, Englisch oder Deutsch. Auch Google-News-Links funktionieren.',
                },
                {
                    title: '„Schlagzeile erstellen“ klicken',
                    desc: 'Oder Enter drücken. Die Anzeige zeigt die einzelnen Schritte; nach 10 bis 30 Sekunden ist das Ergebnis da.',
                },
                {
                    title: 'Ergebnis prüfen',
                    desc: 'Unter „Kurzmeldung“ stehen Schlagzeile, Zusammenfassung und „Quelle öffnen“. Das Kennzeichen „Per Websuche recherchiert“ heißt: Die Seite war nicht lesbar, die Meldung beruht auf einer Recherche zum Thema – Fakten dann besonders prüfen.',
                },
                {
                    title: 'Bearbeiten (optional)',
                    desc: '„Bearbeiten“ macht beides editierbar. Eine Stelle markieren, „Mit KI bearbeiten“ klicken, eine Anweisung eingeben, „Vorschlag erstellen“ und dann „Übernehmen“. „Rückgängig“ nimmt zurück, „Fertig“ beendet.',
                },
                {
                    title: 'Übernehmen',
                    desc: '„Kopieren“ kopiert Schlagzeile und Text, „Word“ („DE“ oder „EN“) im Kopf der Karte lädt beides als Word-Datei.',
                },
            ],
            options: [
                { name: 'Artikel-URL', desc: 'Jeder frei lesbare Nachrichtenartikel, Englisch oder Deutsch.' },
                { name: '„Per Websuche recherchiert“', desc: 'Hinweis, dass die Seite nicht lesbar war und das Thema recherchiert wurde.' },
            ],
            behindTheScenes: [
                'Die Seite wird zuerst direkt gelesen. Ist sie gesperrt oder zu kurz, recherchiert GPT das Thema per Websuche; als letzter Versuch dient ein Browser.',
                'Google-News-Links werden vorher auf den Originalartikel aufgelöst.',
                'Liefert die Seite nur Cookie-Hinweise oder keinen erkennbaren Inhalt, bricht das Tool mit einer Meldung ab, statt zu raten.',
            ],
            tips: [
                'Am besten den Link direkt vom Verlag nehmen.',
                'Für einen ganzen Artikel denselben Link in den Artikel-Generator geben.',
            ],
            troubleshooting: [
                {
                    q: 'Der Google-News-Link lässt sich nicht auflösen',
                    a: 'Den Link im Browser öffnen und die Adresse des Verlags einfügen.',
                },
                {
                    q: 'Die Seite ist gesperrt oder hinter einer Paywall',
                    a: 'Einen frei zugänglichen Bericht zum selben Thema nehmen oder den Artikel-Generator mit der Eingabe „Manuell“ nutzen.',
                },
                {
                    q: 'Es wurde nur Cookie- oder Zustimmungstext gefunden',
                    a: 'Die Seite zeigt automatischen Zugriffen nur ihr Cookie-Banner. Eine andere Quelle wählen.',
                },
            ],
            related: ['headlines', 'generator'],
        },

        revenue: {
            title: 'Revenue News',
            tagline:
                'Quartalszahlen als fertiger HKCM-Artikel: Bericht als Link oder PDF, Kennzahlen direkt aus dem Bericht, Analystenschätzungen per Recherche, Ergebnistabelle und TradingView-Widgets.',
            badge: 'Quartalszahlen',
            time: 'ca. 1,5–2 Min.',
            whenToUse: [
                'In der Berichtssaison, sobald ein Unternehmen seine Zahlen veröffentlicht.',
                'Wenn Umsatz und Gewinn je Aktie mit den Analystenschätzungen verglichen werden sollen.',
            ],
            whatYouGet: [
                'Die Kennzahlen aus dem Bericht: Umsatz, Gewinn je Aktie (EPS), Free Cashflow und operativer Cashflow – mit Schätzung, wo vorhanden.',
                'Einen Artikel mit SEO-Feldern, Teaser, „Das Wichtigste in Kürze“, Ergebnistabelle (Ist-Wert gegen Schätzung, „übertroffen“ oder „verfehlt“) und Abschnitten zu Zahlen, Highlights, Einordnung und Ausblick.',
                'Einen Elliott-Wellen-Hinweis mit Link, eine „Leseempfehlung“, TradingView-Chart und Fundamentaldaten.',
                'Export als Word – mit oder ohne TradingView, auf Deutsch oder Englisch – und als HTML.',
            ],
            steps: [
                {
                    title: 'Quelle wählen',
                    desc: '„URL“ und die Adresse unter „URL des Berichts“ – die Pressemitteilung oder die Investor-Relations-Seite –, oder „PDF“ und „PDF-Datei auswählen“ für den Bericht (bis 30 MB). Optional den Namen unter „Unternehmen (optional)“ eintragen.',
                },
                {
                    title: '„Artikel erstellen“ klicken',
                    desc: 'Oben rechts. Fortschritt und „Protokoll“ zeigen die Schritte: Quelle lesen, Kennzahlen auslesen, Analystenkonsens recherchieren, Artikel schreiben, HKCM-Stil anwenden, Promo, Leseempfehlung und Faktencheck. „Abbrechen“ neben dem Fortschrittsbalken beendet den Lauf.',
                },
                {
                    title: 'Kennzahlen prüfen',
                    desc: 'Die Karten unter „Kennzahlen“ (Umsatz, EPS, Free Cashflow, Operativer Cashflow, jeweils mit „Schätzung“) mit dem Bericht vergleichen. Stimmen sie, stimmt auch die Tabelle im Artikel.',
                },
                {
                    title: 'Artikel lesen',
                    desc: '„Artikelvorschau“ zeigt Wortzahl, Ticker, SEO-Felder und den Artikel. Unter der Tabelle steht, woher die Schätzungen stammen („Schätzungen: Analystenkonsens laut …“) oder dass keine gefunden wurden. „Mobile Vorschau“ zeigt die Handy-Ansicht.',
                },
                {
                    title: 'Bearbeiten (optional)',
                    desc: '„Bearbeiten“ macht SEO-Felder, Teaser, Kernpunkte, Zwischenüberschriften, Texte und die Leseempfehlung editierbar. Eine Stelle markieren, „Mit KI bearbeiten“, „Vorschlag erstellen“ und „Übernehmen“. „Rückgängig“ nimmt zurück, „Fertig“ beendet. Tabelle, Elliott-Block, Promo und Widgets bleiben fest.',
                },
                {
                    title: 'Exportieren',
                    desc: '„Word“ enthält die TradingView-Widgets, „Word ohne TradingView“ ist für das CMS – jeweils „DE“ oder „EN“; „HTML kopieren“ kopiert den Artikel als HTML. Die Einbettungscodes stehen unter „TradingView-Einbettungscodes“.',
                },
            ],
            options: [
                { name: 'URL / PDF', desc: 'Pressemitteilung (z. B. GlobeNewswire oder die IR-Seite) oder der Bericht als PDF.' },
                { name: 'Unternehmen (optional)', desc: 'Hilft bei mehrdeutigen Namen und bei der Zuordnung des Tickers.' },
                { name: '„Word ohne TradingView“', desc: 'Word-Datei ohne Widgets – für Systeme, die sie nicht darstellen.' },
                { name: '„HTML kopieren“', desc: 'Kopiert den Artikel als HTML – formatiert für Editoren, als Quelltext für HTML-Felder.' },
            ],
            behindTheScenes: [
                'Die Kennzahlen stammen nur aus dem Bericht selbst – einschließlich der Angabe, ob der Gewinn je Aktie bereinigt oder nach GAAP ausgewiesen ist.',
                'Die Analystenschätzungen sucht GPT gezielt per Websuche, abgestimmt auf Geschäftsquartal, Stichtag und EPS-Art. Ob übertroffen oder verfehlt, wird danach berechnet, nicht geschätzt.',
                'Der Artikel entsteht aus diesen Zahlen; danach werden Formate vereinheitlicht und der Text gegen den Bericht abgeglichen.',
            ],
            tips: [
                'Beste Quelle ist die offizielle Pressemitteilung oder das PDF des Berichts – nicht ein Nachrichtenartikel darüber.',
                '„N/A“ bei der Schätzung heißt: kein verlässlicher Konsens gefunden. Der Artikel vergleicht dann nicht.',
                'Berichte in Euro oder nach IFRS besonders sorgfältig prüfen – das Tool ist vor allem mit US-Berichten erprobt.',
                'Neu laden verliert das Ergebnis – vorher exportieren. Beim Tool-Wechsel bleibt es erhalten.',
            ],
            troubleshooting: [
                {
                    q: 'Eine Kennzahl stimmt nicht',
                    a: 'Im Bericht nachsehen und den Text über „Bearbeiten“ korrigieren. Die Tabelle lässt sich nicht bearbeiten; bei falschen Werten mit dem PDF des Berichts neu erzeugen.',
                },
                {
                    q: 'Keine Analystenschätzungen gefunden',
                    a: 'Unter der Tabelle steht dann „Kein Analystenkonsens gefunden“. Das kommt bei kleineren Werten oder ungewöhnlichen Stichtagen vor; der Artikel verzichtet auf den Vergleich.',
                },
                {
                    q: 'Das CMS zeigt die TradingView-Widgets nicht',
                    a: '„Word ohne TradingView“ verwenden.',
                },
                {
                    q: 'Die Seite lässt sich nicht laden',
                    a: 'Den Bericht als PDF herunterladen und über „PDF“ hochladen.',
                },
            ],
            related: ['generator', 'formalia', 'seo'],
        },

        formalia: {
            title: 'Formalia Check',
            tagline:
                'Prüft einen fertigen Artikel gegen die HKCM-Formalia – Längen, Aufbau, Zahlen-, Prozent- und Währungsformate, Gendern – und glättet auf Wunsch den Stil.',
            badge: 'Qualitätskontrolle',
            time: 'Prüfung sofort · Stil ca. 30 Sek.',
            whenToUse: [
                'Vor jeder Veröffentlichung eines Textes, der nicht aus den Tools stammt oder stark bearbeitet wurde.',
                'Um externe Texte auf die Hausregeln zu bringen.',
            ],
            whatYouGet: [
                'Eine Plakette: Bestanden oder die Zahl der FAIL- und WARN-Befunde.',
                'Kacheln mit Zielbereich, grün, wenn der Wert passt: H1 (20–90 Zeichen), Teaser (90–170 Zeichen, höchstens 200), Text (500–1000 Wörter), Kernpunkte (3), FAQ (0–4, optional) und Meta-Titel (30–60 Zeichen).',
                'Jeden Befund mit Regel, Fundstelle und Textausschnitt.',
                'Einen Word-Export auf Deutsch oder Englisch (HKCM_Artikel_<Datum>.docx).',
            ],
            steps: [
                {
                    title: 'Text einfügen',
                    desc: 'Am einfachsten über „Word hochladen“ (.docx) – Titel, Teaser, Kernpunkte, Text, FAQ und SEO-Felder werden erkannt. Oder die Felder einzeln füllen: Titel (H1), Teaser, Kernpunkte (ein Punkt pro Zeile), Text (Zwischenüberschriften mit ##), FAQ („Frage: …“ und „Antwort: …“), Meta-Titel, Kategorie, Meta-Beschreibung und Tags.',
                },
                {
                    title: 'Struktur erkennen lassen (bei Einfügen)',
                    desc: 'Nach dem Einfügen aus Word „Struktur erkennen“ klicken, damit Zwischenüberschriften erkannt werden. Stehen alle Kernpunkte in einer Zeile, trennt „Punkte trennen“ sie auf.',
                },
                {
                    title: '„Formalia prüfen“ klicken',
                    desc: 'Oben rechts. Das Ergebnis ist sofort da: Bestanden oder z. B. „2 FAIL“ und „3 WARN“, darunter jeder Befund mit Fundstelle.',
                },
                {
                    title: 'Korrigieren',
                    desc: 'Stellen direkt in den Feldern ändern. Für KI-Hilfe eine Stelle markieren oder in ein Feld klicken und „Mit KI bearbeiten“ wählen – ohne Markierung ersetzt der Vorschlag das ganze Feld. „Stil anpassen“ glättet Sprache und Formate im ganzen Artikel – solange es läuft, sind die Felder gesperrt; „Stil angepasst“ listet die Änderungen. „Rückgängig“ nimmt zurück.',
                },
                {
                    title: '„Erneut prüfen“, bis kein FAIL mehr übrig ist',
                    desc: 'FAIL muss vor der Veröffentlichung behoben sein, WARN ist eine Empfehlung.',
                },
                {
                    title: 'Exportieren',
                    desc: '„Word“ („DE“ oder „EN“) lädt den Artikel als Word-Datei; Titel und Text sind Pflicht.',
                },
            ],
            options: [
                { name: '„Word hochladen“', desc: 'Liest eine .docx-Datei ein und verteilt sie auf die Felder.' },
                { name: '„Struktur erkennen“', desc: 'Markiert nach dem Einfügen die Zwischenüberschriften mit ## und erkennt Kernpunkte und FAQ.' },
                { name: '„Punkte trennen“', desc: 'Trennt zusammengeklebte Kernpunkte in einzelne Zeilen; erscheint neben „Kernpunkte“, wenn alle Punkte in einer Zeile stehen.' },
                { name: '„Formalia prüfen“ / „Erneut prüfen“', desc: 'Prüft die Regeln; das Ergebnis ist sofort da.' },
                { name: '„Stil anpassen“', desc: 'Glättet Sprache und Formate per KI und ändert dabei den Text. Bis die KI fertig ist, sind die Felder gesperrt, damit nichts Getipptes überschrieben wird.' },
                { name: '„Mit KI bearbeiten“', desc: 'Bearbeitet die markierte Stelle im zuletzt angeklickten Feld nach eigener Anweisung, ohne Markierung das ganze Feld.' },
                { name: '„Rückgängig“', desc: 'Nimmt die letzten Änderungen Schritt für Schritt zurück.' },
            ],
            behindTheScenes: [
                'FAIL (muss behoben werden): Gendern, Punkt als Tausendertrenner („10.000“ statt „10 000“), unplausible Größen („14 191 Milliarden“), Prozentzeichen statt „Prozent“, Währungssymbole ($, €, £, ¥), persönliche Handlungsaufforderungen wie „jetzt kaufen“, Titel über 90 Zeichen, Teaser über 200 Zeichen, Meta-Titel über 60 und Meta-Beschreibung über 160 Zeichen, weniger als drei Kernpunkte, Text unter 500 Wörtern.',
                'WARN (Empfehlung): „Dollar“ statt „US-Dollar“, Dezimalkomma statt Punkt („2,5“ statt „2.5“), Kürzel wie „5B“, „—“ oder „ - “ statt „ – “, Duzen, Sätze über 40 Wörter, sehr kurze oder lange Absätze und Zwischenüberschriften, mehr als drei Kernpunkte, mehr als vier FAQ-Einträge oder eine Frage ohne Antwort, eine fehlende oder unbekannte Kategorie, Text über 1000 Wörter, Teaser über 170 Zeichen, kurze Titel, Teaser oder Meta-Angaben.',
                'Die FAQ ist optional: Ohne FAQ gibt es keinen Befund. Die Kategorie muss eine der sieben CMS-Kategorien sein: Global Markets, Deutschland, Finanzbildung, Trendmärkte, Technologie, Kryptowährungen, Rohstoffe und Energie.',
                'Die Prüfung ist regelbasiert: Derselbe Text ergibt immer dasselbe Ergebnis, ohne KI.',
            ],
            tips: [
                '„Word hochladen“ erhält die Struktur besser als Kopieren und Einfügen.',
                'Erst prüfen und gezielt korrigieren, „Stil anpassen“ danach – es formuliert den ganzen Text um.',
                'Die Kacheln sind Richtwerte; entscheidend sind die FAIL-Befunde.',
            ],
            troubleshooting: [
                {
                    q: '„Bitte zuerst den Artikeltext eingeben.“',
                    a: 'Das Textfeld ist leer. Text einfügen oder „Word hochladen“ nutzen.',
                },
                {
                    q: '„Titel und Artikeltext sind erforderlich.“',
                    a: 'Für den Export müssen Titel und Text ausgefüllt sein.',
                },
                {
                    q: 'Zwischenüberschriften werden nicht erkannt',
                    a: 'Sie müssen mit ## beginnen. „Struktur erkennen“ klicken oder die Word-Datei hochladen.',
                },
            ],
            related: ['seo', 'find_similar', 'generator'],
        },

        seo: {
            title: 'SEO Optimization',
            tagline: 'Analysiert einen Artikel auf seine Chancen in Suchmaschinen und setzt ausgewählte Verbesserungen automatisch um.',
            badge: 'Suchmaschinen',
            time: 'ca. 20–60 Sek.',
            whenToUse: [
                'Bevor ein Artikel veröffentlicht wird, der bei Google gefunden werden soll.',
                'Um Meta-Titel, Meta-Beschreibung, Zwischenüberschriften und Suchbegriffe zu verbessern.',
            ],
            whatYouGet: [
                'Vorschläge nach Priorität: „Kritisch“, „Empfohlen“ und „Optional“.',
                'Die ausgewählten Vorschläge direkt im Text umgesetzt.',
                'Einen Word-Export auf Deutsch oder Englisch (HKCM_Artikel_<Datum>.docx).',
            ],
            steps: [
                {
                    title: 'Text einfügen',
                    desc: '„Word hochladen“ (.docx) oder die Felder einzeln füllen: Titel, Teaser, Kernpunkte, Text, FAQ und SEO-Felder. Nach dem Einfügen hilft „Struktur erkennen“.',
                },
                {
                    title: '„SEO analysieren“ klicken',
                    desc: 'Oben rechts. Unter „SEO-Vorschläge“ erscheinen die Vorschläge, sortiert nach „Kritisch“, „Empfohlen“ und „Optional“.',
                },
                {
                    title: 'Vorschläge auswählen',
                    desc: 'Die gewünschten Vorschläge ankreuzen – „Kritisch“ zuerst.',
                },
                {
                    title: '„Verbesserungen übernehmen“ klicken',
                    desc: 'Die KI setzt die Auswahl im Text um („Verbesserungen übernommen.“); solange sie arbeitet, sind die Felder gesperrt. Hätte sie den Text stark gekürzt, bleibt er unverändert, und ein Hinweis sagt es. Das Ergebnis in den Feldern prüfen; „Mit KI bearbeiten“ hilft bei einzelnen Stellen.',
                },
                {
                    title: 'Exportieren',
                    desc: '„Word“ („DE“ oder „EN“) lädt den Artikel als Word-Datei; Titel und Text sind Pflicht.',
                },
            ],
            options: [
                { name: '„Word hochladen“ / „Struktur erkennen“', desc: 'Text aus einer .docx-Datei übernehmen bzw. nach dem Einfügen die Struktur erkennen.' },
                { name: '„SEO analysieren“', desc: 'Analysiert den Artikel und liefert Vorschläge.' },
                { name: '„Verbesserungen übernehmen“', desc: 'Setzt die angekreuzten Vorschläge im Text um.' },
                { name: '„Mit KI bearbeiten“', desc: 'Bearbeitet die markierte Stelle im zuletzt angeklickten Feld nach eigener Anweisung, ohne Markierung das ganze Feld.' },
            ],
            behindTheScenes: [
                'Ein schnelles Modell bewertet Titel, Teaser, Kernpunkte, Text, FAQ und Meta-Angaben für deutschsprachige Finanzsuchen und hält dabei den HKCM-Stil ein.',
                'Die Umsetzung übernimmt das stärkste Modell – nur für die angekreuzten Punkte, der Rest des Textes bleibt, wie er ist.',
            ],
            tips: [
                'Meta-Titel 30–60 Zeichen, Meta-Beschreibung 100–160 Zeichen.',
                'Nach „Verbesserungen übernehmen“ den Text im Formalia Check noch einmal prüfen.',
                'Nicht jeden Vorschlag übernehmen: Lesbarkeit geht vor Suchbegriffen.',
            ],
            troubleshooting: [
                {
                    q: '„Bitte zuerst den Artikeltext eingeben.“',
                    a: 'Das Textfeld ist leer. Text einfügen oder „Word hochladen“ nutzen.',
                },
                {
                    q: '„Bitte mindestens einen Vorschlag auswählen.“',
                    a: 'Vor „Verbesserungen übernehmen“ mindestens einen Vorschlag ankreuzen.',
                },
            ],
            related: ['formalia', 'find_similar'],
        },

        infographics: {
            title: 'News-Grafiken',
            tagline:
                'Erstellt zu einem bestehenden Artikel Infografiken im HKCM-Design – jede zeigt einen Kernaspekt, gesetzt auf dem 1500 × 844-Raster der Vorgabe und geliefert in 4K (3840 × 2160 Pixel), mit einheitlichem Logo und Farben.',
            badge: 'Grafiken',
            time: 'ca. 1 Min.',
            whenToUse: [
                'Wenn ein Artikel Zahlen, Abläufe oder Vergleiche enthält, die sich als Grafik besser erklären lassen.',
                'Für Social Media und Artikelbilder im einheitlichen HKCM-Look.',
            ],
            whatYouGet: [
                'Zwei bis drei Grafiken (oder eine feste Anzahl von 1 bis 4), jede zu einem Kernaspekt des Artikels.',
                'Zu jeder Grafik Kernaspekt, Aufbau, Hauptelement, Bildunterschrift und gegebenenfalls Hinweise zur Prüfung.',
                'Jede Grafik als PNG, einzeln oder alle auf einmal.',
            ],
            steps: [
                {
                    title: 'Artikel einfügen',
                    desc: 'Den Artikeltext einfügen (Zwischenüberschriften mit ##) oder „Word hochladen“. Optional „Titel“ und „Quelle“ eintragen – die Quelle erscheint auf den Grafiken.',
                },
                {
                    title: 'Anzahl wählen',
                    desc: '„Automatisch (2 bis 3)“ oder eine feste Anzahl von 1 bis 4.',
                },
                {
                    title: '„Grafiken erstellen“ klicken',
                    desc: 'Das dauert etwa eine halbe bis eine Minute; der Balken zeigt, wie weit der Lauf ist, „Abbrechen“ beendet ihn. Die Seite darf dabei verlassen werden – der Lauf geht weiter.',
                },
                {
                    title: 'Prüfen',
                    desc: 'Jede Karte zeigt die Grafik mit Kernaspekt, Aufbau und Bildunterschrift; ein Klick vergrößert sie. Hinweise auf der Karte zeigen, was geprüft werden sollte.',
                },
                {
                    title: 'Herunterladen',
                    desc: '„PNG“ lädt eine Grafik, „Alle herunterladen“ alle.',
                },
            ],
            options: [
                { name: 'Titel (optional)', desc: 'Überschrift des Artikels; hilft bei der Auswahl der Kernaspekte.' },
                { name: 'Quelle (optional)', desc: 'Wird als Quellenangabe auf den Grafiken gezeigt.' },
                { name: 'Anzahl', desc: '„Automatisch (2 bis 3)“ oder 1 bis 4.' },
                { name: '„Word hochladen“', desc: 'Übernimmt Titel und Text aus einer Word-Datei (.docx).' },
            ],
            behindTheScenes: [
                'Ein Modell wählt die Kernaspekte und passende Darstellungen – etwa Ablauf, Zeitstrahl, Linie, Balken, Anteil, Kennzahl oder Zitat – und verwendet dabei nur Zahlen aus dem Text.',
                'Den Aufbau setzt der Code: Jede Fläche bekommt eigenen Inhalt, Diagramme entstehen aus den Werten – liegen sie dicht beieinander, als Punkte auf einer Achse, deren Beginn angegeben ist –, keine Zahl steht doppelt, und lange Wörter werden an der Silbe getrennt.',
                'Gesetzt werden die Grafiken als Layout im Browser, nicht von einem Bildmodell. Deshalb stimmen Zahlen, Texte, Farben, Schriften, Logo und Maße immer exakt.',
                'Wie in den Vorlagen der Redaktion: eine Themenzeile über der Überschrift, zu jeder Kennzahl und jedem Punkt ein Symbol, Grün für einen Gewinn und Rot für einen Verlust oder ein Risiko. Neben einer breiten Überschrift steht eine kleine 3D-Illustration des Themas – sie zeichnet das Bildmodell, ohne Schrift und ohne Zahlen; misslingt sie, erscheint die Grafik ohne.',
            ],
            tips: [
                'Der Artikel-Generator erstellt Grafiken direkt mit – „News-Grafiken erstellen“ ankreuzen – und nimmt sie in den Word-Bericht auf.',
                'Texte mit konkreten Zahlen ergeben die aussagekräftigsten Grafiken.',
                'Jede Zahl auf der Grafik noch einmal mit dem Artikel vergleichen.',
            ],
            troubleshooting: [
                {
                    q: '„Grafiken erstellen“ lässt sich nicht klicken',
                    a: 'Das Textfeld ist leer. Text einfügen oder eine Word-Datei hochladen.',
                },
                {
                    q: 'Die Grafiken konnten nicht erstellt werden',
                    a: 'Noch einmal versuchen. Hilft das nicht, einen kürzeren Text oder eine feste Anzahl wählen.',
                },
            ],
            related: ['generator', 'formalia'],
        },

        find_similar: {
            title: 'Find Similar Links',
            tagline:
                'Findet passende Links für einen Artikel – im Web, im HKCM-Archiv, bei den HKCM-Produkten und in der deutschen Presse – und setzt die ausgewählten per KI in den Text.',
            badge: 'Verlinkung',
            time: 'ca. 10–40 Sek. je Suche',
            whenToUse: [
                'Vor dem Veröffentlichen, um interne HKCM-Links und passende Produkte zu setzen.',
                'Um einen Artikel mit weiterführenden Quellen anzureichern.',
            ],
            whatYouGet: [
                'Vier getrennte Trefferlisten: Web-Artikel, HKCM-News, HKCM-Produkte und deutsche Nachrichten.',
                'Eine Vorschau, in der die KI die ausgewählten Links an passenden Stellen eingesetzt hat.',
                'Einen Word-Export auf Deutsch oder Englisch (HKCM_Artikel_<Datum>.docx).',
            ],
            steps: [
                {
                    title: 'Text einfügen',
                    desc: '„Word hochladen“ (.docx) oder die Felder einzeln füllen. Nach dem Einfügen hilft „Struktur erkennen“.',
                },
                {
                    title: 'Suchen',
                    desc: 'Im Bereich „Ähnliche Artikel und Produkte“ auf „Web-Artikel suchen“, „HKCM-News suchen“, „HKCM-Produkte suchen“ oder „Deutsche Nachrichten suchen“ klicken. Jede Suche läuft einzeln und füllt ihre eigene Liste.',
                },
                {
                    title: 'Links auswählen',
                    desc: 'Passende Treffer ankreuzen; darunter steht, wie viele ausgewählt sind. „Auswahl aufheben“ setzt die Auswahl zurück.',
                },
                {
                    title: 'Vorschau ansehen',
                    desc: '„Links einsetzen“ zeigt unter „Vorschau“ den Text mit den eingesetzten Links; solange die KI sie setzt, sind die Felder gesperrt. Passt es, „Übernehmen“ klicken („Links eingefügt.“), sonst „Verwerfen“. Wurde der Text danach noch geändert, übernimmt „Übernehmen“ nichts und sagt es – dann „Links einsetzen“ noch einmal klicken.',
                },
                {
                    title: 'Exportieren',
                    desc: '„Word“ („DE“ oder „EN“) lädt den angereicherten Artikel als Word-Datei.',
                },
            ],
            options: [
                { name: '„Web-Artikel suchen“', desc: 'Aktuelle Berichte aus dem Web zum selben Thema.' },
                { name: '„HKCM-News suchen“', desc: 'Passende Artikel aus dem HKCM-Archiv.' },
                { name: '„HKCM-Produkte suchen“', desc: 'Passende HKCM-Produkte, z. B. für einen Promo-Hinweis.' },
                { name: '„Deutsche Nachrichten suchen“', desc: 'Deutschsprachige Finanzpresse zum Thema.' },
                { name: '„Links einsetzen“ / „Übernehmen“', desc: 'Erst ansehen, dann übernehmen – ohne „Übernehmen“ ändert sich nichts.' },
            ],
            behindTheScenes: [
                'Die Suchbegriffe werden aus Titel und Text abgeleitet. HKCM-News kommen aus dem HKCM-Archiv, Produkte aus dem HKCM-Produktkatalog, Web- und deutsche Nachrichten aus der Nachrichtensuche – mit GPT-Websuche als Rückfallebene.',
                'Beim Einsetzen wählt die KI passende Ankertexte und Stellen im Text; der übrige Text bleibt unverändert.',
            ],
            tips: [
                'Ein bis zwei interne HKCM-Links pro Artikel reichen meist.',
                'In der Vorschau die Ankertexte prüfen – sie sollen natürlich im Satz stehen.',
                'Danach den Text im Formalia Check noch einmal prüfen.',
            ],
            troubleshooting: [
                {
                    q: 'Eine Liste bleibt leer',
                    a: 'Zu diesem Thema gab es in dieser Quelle nichts Passendes. Die anderen Suchen nutzen oder den Titel konkreter fassen.',
                },
                {
                    q: '„Titel und Artikeltext sind erforderlich.“',
                    a: 'Für den Export müssen Titel und Text ausgefüllt sein.',
                },
            ],
            related: ['seo', 'formalia'],
        },
    },

    troubleshooting: {
        title: 'Probleme lösen',
        intro: 'Die häufigsten Meldungen und was zu tun ist. Tool-spezifische Fälle stehen auch auf der jeweiligen Tool-Seite.',
        sections: [
            {
                title: 'Anmeldung',
                items: [
                    {
                        q: '„Anmeldung abgelaufen“',
                        a: 'Die Anmeldung ist abgelaufen. Im roten Hinweis oben „Erneut anmelden“ klicken und im Fenster anmelden – die Seite, die Eingaben und laufende Aufträge bleiben erhalten, danach funktioniert alles wieder. Öffnet sich kein Fenster, Pop-ups für die Seite erlauben oder „Abmelden“ klicken und neu anmelden.',
                    },
                    {
                        q: '„… konnte nicht gestartet werden …“',
                        a: 'Meist ebenfalls eine abgelaufene Anmeldung: „Erneut anmelden“, dann erneut starten.',
                    },
                ],
            },
            {
                title: 'Quellen und Webseiten',
                items: [
                    {
                        q: 'Eine Seite lässt sich nicht laden oder ist gesperrt',
                        a: 'Die Seite blockiert automatische Zugriffe oder hat eine Paywall. Den Artikel im Browser öffnen, Text kopieren und selbst einfügen (Artikel-Generator: „Manuell“) – oder eine andere Quelle wählen.',
                    },
                    {
                        q: 'Ein Google-News-Link funktioniert nicht',
                        a: 'Artikel-Generator und Single Headline lösen Google-News-Links selbst auf. Klappt das nicht, den Link im Browser öffnen und die Adresse des Verlags verwenden.',
                    },
                    {
                        q: 'Das Ergebnis beruht auf Cookie-Text oder wirkt thematisch falsch',
                        a: 'Die Seite hat automatischen Zugriffen nur ein Cookie-Banner gezeigt. Eine andere Quelle wählen oder den Text selbst einfügen.',
                    },
                ],
            },
            {
                title: 'Laufende Aufträge',
                items: [
                    {
                        q: 'Nach dem Neuladen ist das Ergebnis weg',
                        a: 'Beim Tool-Wechsel bleibt jedes Ergebnis erhalten, beim Neuladen der Seite nicht (Ausnahme: Newsflash). Vor dem Neuladen exportieren.',
                    },
                    {
                        q: 'Die Seite wurde während eines Laufs neu geladen',
                        a: 'Die Seite verliert dabei die Verbindung zum Auftrag. Den Lauf neu starten. Bei Headlines und Crypto Headlines kann der neue Lauf Geschichten überspringen, die der abgebrochene schon verarbeitet hat.',
                    },
                    {
                        q: '„Der Server wurde neu gestartet …“ oder „… nicht mehr erreichbar“',
                        a: 'Der Server wurde während des Laufs neu gestartet, etwa bei einem Update. Einfach neu starten.',
                    },
                ],
            },
            {
                title: 'Ergebnisse und Zahlen',
                items: [
                    {
                        q: 'Eine Zahl oder ein Name stimmt nicht',
                        a: 'Gegen die Originalquelle prüfen und im Text korrigieren. Der Faktencheck fängt eindeutige Fehler ab, ersetzt aber nicht die Abnahme.',
                    },
                    {
                        q: 'Die Formalia-Plakette zeigt FAIL',
                        a: 'FAIL muss vor der Veröffentlichung behoben werden, z. B. Gendern, „10.000“ statt „10 000“ oder ein Prozentzeichen. WARN ist eine Empfehlung. Stelle korrigieren und erneut prüfen.',
                    },
                    {
                        q: 'Revenue News zeigt „N/A“ bei den Schätzungen',
                        a: 'Es wurde kein verlässlicher Analystenkonsens gefunden. Der Artikel verzichtet dann auf den Vergleich.',
                    },
                ],
            },
            {
                title: 'Export',
                items: [
                    {
                        q: 'Der Word-Export schlägt fehl',
                        a: 'Noch einmal versuchen. In Formalia Check, SEO Optimization und Find Similar Links müssen Titel und Text ausgefüllt sein.',
                    },
                    {
                        q: '„Die Übersetzung ins Englische ist fehlgeschlagen.“',
                        a: 'Noch einmal „EN“ klicken. Die Übersetzung dauert einige Sekunden, bei langen Dokumenten bis zu einer Minute. Die deutsche Fassung („DE“) ist davon nicht betroffen.',
                    },
                    {
                        q: 'Das CMS zeigt TradingView-Widgets nicht',
                        a: 'In Revenue News „Word ohne TradingView“ verwenden.',
                    },
                ],
            },
        ],
    },

    glossary: {
        title: 'Glossar',
        intro: 'Die wichtigsten Begriffe aus Redaktion, SEO und den Tools. Viele davon erscheinen in den Tools auch als Hinweis am (?)-Symbol.',
    },
}
